// ----------------------------------------------------------------------------
//   注文フォームの「年・月・日」3セレクトで、UI上から昨日以前を選べないよう制御する。
//   - 今年を選ぶと「今月以降」の月のみ表示
//   - 今月を選ぶと「今日以降」の日のみ表示
//   - 来年以降は 1〜12月 / 1〜末日を表示
//   - 初期表示は helper 側で今日以降に絞込、以降の動的変更は本TSで差し替え
// ----------------------------------------------------------------------------

const onReady = (fn: () => void) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn);
  } else {
    fn();
  }
  document.addEventListener('turbo:load', fn as EventListener);
  document.addEventListener('turbolinks:load', fn as EventListener);
};


// start..end の整数レンジを文字列配列で生成（例: range(3,5) => ["3","4","5"]）
const range = (start: number, end: number): string[] =>
  Array.from({ length: Math.max(0, end - start + 1) }, (_, i) =>
    String(start + i)
  );


// 指定の年・月(1..12)の末日を返す
// new Date(year, month, 0) は「month の前月末」を返す性質を利用
const lastDayOf = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();


// .desired-group 内から役割(role)に対応する <select> を取得
// 取得要素が select でない可能性に備え、instanceof で型ガードして安全に返す
const getSelect = (
  group: Element,
  role: 'year' | 'month' | 'day'
): HTMLSelectElement | null => {
  const el = group.querySelector(`select[data-role="${role}"]`);
  return el instanceof HTMLSelectElement ? el : null;
};


// セレクト要素の <option> を与えられた values で再構築する
// 既存のプロンプト(option[value=""]) があれば温存、なければ "-" をプロンプトとして追加
// keepValue が有効で候補に含まれる場合は、その値を選択状態に維持
const setOptions = (
  selectEl: HTMLSelectElement,
  values: string[],
  keepValue?: string
) => {
  const promptOpt = selectEl.querySelector('option[value=""]');
  const promptHTML = promptOpt
    ? promptOpt.outerHTML
    : '<option value="">-</option>';
  const html = [promptHTML]
    .concat(values.map((v) => `<option value="${v}">${v}</option>`))
    .join('');
  selectEl.innerHTML = html;

  if (keepValue && values.includes(keepValue)) {
    selectEl.value = keepValue;
  }
};

// 年選択の変更を受けて「月/日」を更新
// - 年未選択: 月・日を空に
// - 今年    : 今月〜12月
// - 来年以降: 1月〜12月
// 月の再構築後は日付も現在の年/月に応じて再計算する
const refreshMonth = (group: Element, today: Date) => {
  const yearSel = getSelect(group, 'year');
  const monthSel = getSelect(group, 'month');
  const daySel = getSelect(group, 'day');
  if (!yearSel || !monthSel || !daySel) return;

  const y = parseInt(yearSel.value, 10);
  if (Number.isNaN(y)) {
    setOptions(monthSel, []);
    setOptions(daySel, []);
    return;
  }

  const startMonth = y === today.getFullYear() ? today.getMonth() + 1 : 1;
  setOptions(monthSel, range(startMonth, 12), monthSel.value);

  // 月候補更新に伴い日も更新
  refreshDay(group, today);
};

// 月選択の変更を受けて「日」を更新
// - 年/月未選択: 日を空に
// - 今年かつ今月: 今日〜末日
// - それ以外   : 1日〜末日
const refreshDay = (group: Element, today: Date) => {
  const yearSel = getSelect(group, 'year');
  const monthSel = getSelect(group, 'month');
  const daySel = getSelect(group, 'day');
  if (!yearSel || !monthSel || !daySel) return;

  const y = parseInt(yearSel.value, 10);
  const m = parseInt(monthSel.value, 10);
  if (Number.isNaN(y) || Number.isNaN(m)) {
    setOptions(daySel, []);
    return;
  }

  const last = lastDayOf(y, m);
  const startDay =
    y === today.getFullYear() && m === today.getMonth() + 1
      ? today.getDate()
      : 1;

  setOptions(daySel, range(startDay, last), daySel.value);
};

// .desired-group 1セット分の初期化:
// - 現在値に基づいて月/日を再構築
// - 年/月に change リスナーを付与
const initGroup = (group: Element, today: Date) => {
  refreshMonth(group, today);
  const yearSel = getSelect(group, 'year');
  const monthSel = getSelect(group, 'month');

  yearSel?.addEventListener('change', () => refreshMonth(group, today));
  monthSel?.addEventListener('change', () => refreshDay(group, today));
};


// 画面ロード時の初期化:
// - 既存の .desired-group を初期化
// - イベント委譲で change を拾い、動的に追加された行にも追従
onReady(() => {
  const today = new Date();
  const selector = '.desired-group';

  document
    .querySelectorAll(selector)
    .forEach((group) => initGroup(group, today));

  document.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLSelectElement)) return;
    const group = target.closest(selector);
    if (!group) return;

    if (target.dataset.role === 'year') {
      refreshMonth(group, today);
    } else if (target.dataset.role === 'month') {
      refreshDay(group, today);
    }
  });
});
